import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getSandbox, SandboxProject } from '../api/sandboxApi';

const THIRTY_MINUTES_MS = 30 * 60 * 1000;

/**
 * TanStack cache keys for everything sandbox-related.
 *
 * `project` is real server state (the user's sandbox project, or null).
 * `mode` and `dismissed` are local UI flags parked in the cache so they
 * survive in-app navigation but evaporate after a full refresh OR after
 * ~30 min of being unobserved \u2014 less persistent than sessionStorage and
 * doesn't touch the DOM storage surface.
 */
export const sandboxQueryKeys = {
  project: ['sandbox', 'project'] as const,
  mode: ['sandbox', 'mode'] as const,
  dismissed: ['sandbox', 'banner-dismissed'] as const,
  // Session sentinel \u2014 flipped true after the first auto-enter check has
  // run for this session so we never re-enter sandbox mode after the user
  // has explicitly exited.
  autoEntered: ['sandbox', 'auto-entered'] as const,
};

function pathContainsProjectId(pathname: string, projectId: number): boolean {
  // Match a project id as a standalone path segment, e.g. /app/property/.../<id>/...
  const id = String(projectId);
  const normalized = pathname.endsWith('/') ? pathname : `${pathname}/`;
  return normalized.includes(`/${id}/`);
}

export interface UseSandboxModeResult {
  sandbox: SandboxProject | null;
  inSandboxMode: boolean;
  enter: () => void;
  exit: () => void;
  isCurrentRouteSandbox: boolean;
  bannerDismissed: boolean;
  dismissBanner: () => void;
  resetBannerDismissal: () => void;
  /**
   * One-shot helper for the dashboard: enters sandbox mode automatically
   * iff the user's client has a sandbox AND no non-sandbox projects.
   * Idempotent within a session \u2014 subsequent calls (and explicit user
   * `exit()` afterwards) are honoured without re-triggering auto-enter.
   */
  maybeAutoEnter: (options: { hasNonSandboxProjects: boolean }) => void;
}

/**
 * Track whether the user is currently working inside their client's sandbox.
 *
 * Sources of truth:
 *   - `sandbox`         \u2014 `useQuery(['sandbox','project'])`, populated by the
 *                          `GET /api/sandbox/` endpoint with a 30 min cache.
 *   - `stored mode`     \u2014 `useQuery(['sandbox','mode'])`, a local boolean kept
 *                          in the TanStack cache (no sessionStorage).
 *   - `dismissed`       \u2014 `useQuery(['sandbox','banner-dismissed'])`, also a
 *                          local boolean in the TanStack cache.
 *
 * "In sandbox mode" is true when EITHER:
 *   - the current URL contains the sandbox project's id as a path segment, OR
 *   - the user explicitly entered sandbox mode via `enter()`.
 *
 * The cache entries live up to 30 minutes (gcTime + staleTime). A full page
 * refresh wipes them outright \u2014 which is what we want; sandbox mode should
 * not feel sticky beyond a single working session.
 */
export function useSandboxMode(): UseSandboxModeResult {
  const location = useLocation();
  const queryClient = useQueryClient();

  // Server state: the user's sandbox project (or null when none).
  const { data: sandbox = null } = useQuery<SandboxProject | null>({
    queryKey: sandboxQueryKeys.project,
    queryFn: async () => (await getSandbox()) ?? null,
    staleTime: THIRTY_MINUTES_MS,
    gcTime: THIRTY_MINUTES_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Local UI flag: explicit "sandbox mode" toggle. Defaults to false.
  const { data: stored = false } = useQuery<boolean>({
    queryKey: sandboxQueryKeys.mode,
    queryFn: () => false,
    staleTime: THIRTY_MINUTES_MS,
    gcTime: THIRTY_MINUTES_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Local UI flag: banner dismissal. Defaults to false.
  const { data: dismissed = false } = useQuery<boolean>({
    queryKey: sandboxQueryKeys.dismissed,
    queryFn: () => false,
    staleTime: THIRTY_MINUTES_MS,
    gcTime: THIRTY_MINUTES_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Session sentinel for `maybeAutoEnter`. See comment on the query key.
  const { data: autoEntered = false } = useQuery<boolean>({
    queryKey: sandboxQueryKeys.autoEntered,
    queryFn: () => false,
    staleTime: THIRTY_MINUTES_MS,
    gcTime: THIRTY_MINUTES_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
  });

  const isCurrentRouteSandbox =
    sandbox !== null && pathContainsProjectId(location.pathname, sandbox.id);

  // Auto-enter sandbox mode only when transitioning INTO a sandbox URL (so the
  // mode persists across subsequent navigation), never on every render \u2014
  // otherwise clicking "Exit sandbox" while still on the sandbox URL would be
  // immediately undone before the navigation away completes.
  const prevWasSandboxRouteRef = useRef<boolean>(isCurrentRouteSandbox);
  useEffect(() => {
    const prev = prevWasSandboxRouteRef.current;
    if (isCurrentRouteSandbox && !prev) {
      queryClient.setQueryData<boolean>(sandboxQueryKeys.mode, true);
    }
    prevWasSandboxRouteRef.current = isCurrentRouteSandbox;
  }, [isCurrentRouteSandbox, queryClient]);

  const enter = useCallback(() => {
    queryClient.setQueryData<boolean>(sandboxQueryKeys.mode, true);
  }, [queryClient]);

  const exit = useCallback(() => {
    queryClient.setQueryData<boolean>(sandboxQueryKeys.mode, false);
  }, [queryClient]);

  const dismissBanner = useCallback(() => {
    queryClient.setQueryData<boolean>(sandboxQueryKeys.dismissed, true);
  }, [queryClient]);

  const resetBannerDismissal = useCallback(() => {
    queryClient.setQueryData<boolean>(sandboxQueryKeys.dismissed, false);
  }, [queryClient]);

  const maybeAutoEnter = useCallback(
    (options: { hasNonSandboxProjects: boolean }) => {
      // Only run once per session. Once the sentinel flips true, any later
      // call (e.g. on dashboard re-mount) is a no-op so an explicit user
      // `exit()` isn't immediately undone.
      if (autoEntered) return;
      // Need a sandbox project to enter, of course. Wait for the query
      // to resolve \u2014 we'll get re-invoked by the caller's effect.
      if (!sandbox) return;

      if (!options.hasNonSandboxProjects) {
        queryClient.setQueryData<boolean>(sandboxQueryKeys.mode, true);
        // Banner dismissal is irrelevant inside sandbox mode (only the
        // active-state banner shows there) but reset it so the next time
        // they exit they see the ad banner again.
        queryClient.setQueryData<boolean>(sandboxQueryKeys.dismissed, false);
      }
      queryClient.setQueryData<boolean>(sandboxQueryKeys.autoEntered, true);
    },
    [autoEntered, sandbox, queryClient]
  );

  // Treating sandbox mode as "off" when no sandbox is visible to this user
  // prevents the sidebar / dashboard from collapsing to an empty list if a
  // stale cache flag outlives a sandbox teardown.
  const inSandboxMode = sandbox !== null && (stored || isCurrentRouteSandbox);

  return {
    sandbox,
    inSandboxMode,
    enter,
    exit,
    isCurrentRouteSandbox,
    bannerDismissed: dismissed,
    dismissBanner,
    resetBannerDismissal,
    maybeAutoEnter,
  };
}
